/** Turn Amplify / GraphQL / unknown errors into a readable string for the UI. */
export function formatUnknownError(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  if (typeof err === 'string') return err

  if (Array.isArray(err)) {
    return err.map((item) => formatUnknownError(item)).join('; ')
  }

  if (err && typeof err === 'object') {
    const rec = err as Record<string, unknown>

    if (Array.isArray(rec.errors)) {
      return rec.errors.map((item) => formatUnknownError(item)).join('; ')
    }

    if (typeof rec.message === 'string' && rec.message) return rec.message
  }

  try {
    return JSON.stringify(err)
  } catch {
    return 'Something went wrong'
  }
}

/**
 * Raw AWS/AppSync infrastructure errors (e.g. a throttled Lambda invoke
 * surfacing as `software.amazon.awssdk...TooManyRequestsException: Rate
 * Exceeded. (Service: Lambda, Status Code: 429...)`) must never be shown to
 * users verbatim — they read like gibberish and leak implementation details.
 */
const AWS_INTERNAL_ERROR_PATTERN =
  /software\.amazon|amazonaws\.com|TooManyRequestsException|Rate Exceeded|Status Code: \d{3}|Lambda:Invoke|ExecutionTimeout|ServiceException/i

/**
 * Format an unknown error for user display, replacing AWS-internal error text
 * with a human fallback. App-authored messages ("Not a participant…", "You
 * cannot add…") pass through unchanged.
 */
export function formatUserFacingError(err: unknown, fallback: string): string {
  const raw = formatUnknownError(err)
  if (!raw || AWS_INTERNAL_ERROR_PATTERN.test(raw)) return fallback
  return raw
}

/** Server refused a call signal because this Cognito user is not on the session. */
export function isNotParticipantError(err: unknown): boolean {
  return /not a participant/i.test(formatUnknownError(err))
}

/** Server refused an INVITE for a call that already has a terminal record. */
export function isCallAlreadyEndedError(err: unknown): boolean {
  return /call already ended/i.test(formatUnknownError(err))
}
